import { addBoundary } from '@apia/util';
import { Controller2 } from './Controller2';
import { applySelectionAttributes } from './applySelectionAttributes';
import { applyFocusAttributes } from './applyFocusAttributes';

export function handleKeyDownEvent(this: Controller2, ev: KeyboardEvent) {
  const target = ev.target;
  const state = this.getState();

  if (target instanceof HTMLElement) {
    const table = target.closest('table');
    const row = target.closest('tr');
    const cell = target.closest('th,td');
    if (table && row) {
      if (state.isEditionMode) {
        if (ev.code === 'Escape' || ev.code === 'Enter') {
          state.isEditionMode = false;
          this.setState(state);
          applyFocusAttributes(table, this.getState(), state);
        }
      } else {
        const rowIndex = Number.parseInt(row.dataset.rowindex || 'a', 10);
        const colIndex = Number.parseInt(cell?.ariaColIndex || '0', 10);
        if (!isNaN(rowIndex) && !isNaN(colIndex)) {
          if (
            [
              'ArrowDown',
              'ArrowUp',
              'ArrowLeft',
              'ArrowRight',
              'End',
              'Enter',
              'Home',
              'PageUp',
              'PageDown',
            ].includes(ev.code) ||
            (ev.code === 'Space' && ev.ctrlKey)
          ) {
            ev.preventDefault();
            switch (ev.code) {
              case 'Space':
                if (state.selectedRows.includes(rowIndex)) {
                  state.selectedRows = state.selectedRows.filter(
                    (c) => c !== rowIndex,
                  );
                } else {
                  state.selectedRows = [...state.selectedRows, rowIndex];
                }
                break;
              case 'ArrowDown':
                state.focusedRow = addBoundary(
                  state.focusedRow + 1,
                  state.hasNonAdditionalFilters ? -2 : -1,
                  state.rows.length - 1,
                );
                if (ev.ctrlKey) {
                  //
                } else if (ev.shiftKey && state.isMultiple !== false) {
                  state.selectedRows = [
                    ...state.selectedRows,
                    state.focusedRow,
                  ];
                } else {
                  state.selectedRows = [state.focusedRow];
                }
                break;
              case 'ArrowUp':
                state.focusedRow = addBoundary(
                  state.focusedRow - 1,
                  state.hasNonAdditionalFilters ? -2 : -1,
                  state.rows.length - 1,
                );
                if (ev.ctrlKey) {
                  //
                } else if (ev.shiftKey && state.isMultiple !== false) {
                  state.selectedRows = [
                    ...state.selectedRows,
                    state.focusedRow,
                  ];
                } else {
                  state.selectedRows = [state.focusedRow];
                }
                break;
              case 'ArrowLeft':
                state.focusedColumn = addBoundary(
                  state.focusedColumn - 1,
                  0,
                  state.columns.length,
                );
                break;
              case 'ArrowRight':
                state.focusedColumn = addBoundary(
                  state.focusedColumn + 1,
                  0,
                  state.columns.length +
                    (state.hasNonAdditionalFilters ? 1 : 0),
                );
                break;
              case 'Home':
                /**
              
               * If focus is on a row, moves focus to the first row. If focus is in the first row, focus does not move.
               
               * If focus is on a cell, moves focus to the first cell in the row. If focus is in the first cell of the row, focus does not move.
            
             */
                if (state.focusedColumn === 0) {
                  state.focusedRow = state.hasNonAdditionalFilters ? -2 : -1;
                  if (ev.ctrlKey) {
                    //
                  } else if (ev.shiftKey && state.isMultiple !== false) {
                    const max = Math.max(
                      state.focusedRow,
                      this.getState().focusedRow,
                    );
                    const min = Math.min(
                      state.focusedRow,
                      this.getState().focusedRow,
                    );
                    for (let i = min; i <= max; i++) {
                      state.selectedRows.push(i);
                    }
                  } else {
                    state.selectedRows = [state.focusedRow];
                  }
                } else {
                  state.focusedColumn = 1;
                }
                break;
              case 'End':
                if (state.focusedColumn === 0) {
                  state.focusedRow = state.rows.length - 1;
                  if (ev.ctrlKey) {
                    //
                  } else if (ev.shiftKey && state.isMultiple !== false) {
                    const max = Math.max(
                      state.focusedRow,
                      this.getState().focusedRow,
                    );
                    const min = Math.min(
                      state.focusedRow,
                      this.getState().focusedRow,
                    );
                    for (let i = min; i <= max; i++) {
                      state.selectedRows.push(i);
                    }
                  } else {
                    state.selectedRows = [state.focusedRow];
                  }
                } else {
                  state.focusedColumn = state.columns.length;
                }
                break;
              case 'PageUp':
                state.focusedRow = addBoundary(
                  state.focusedRow - 6,
                  state.hasNonAdditionalFilters ? -2 : -1,
                  state.rows.length - 1,
                );
                if (ev.ctrlKey) {
                  //
                } else if (ev.shiftKey && state.isMultiple !== false) {
                  const previousFocused = this.getState().focusedRow;
                  state.selectedRows = [...state.selectedRows];

                  const min = Math.min(previousFocused, state.focusedRow);
                  const max = Math.max(previousFocused, state.focusedRow);

                  for (let i = min; i <= max; i++) {
                    state.selectedRows.push(i);
                  }
                } else {
                  state.selectedRows = [state.focusedRow];
                }
                break;
              case 'PageDown':
                state.focusedRow = addBoundary(
                  state.focusedRow + 6,
                  state.hasNonAdditionalFilters ? -2 : -1,
                  state.rows.length - 1,
                );
                if (ev.ctrlKey) {
                  //
                } else if (ev.shiftKey && state.isMultiple !== false) {
                  const previousFocused = this.getState().focusedRow;
                  state.selectedRows = [...state.selectedRows];

                  const min = Math.min(previousFocused, state.focusedRow);
                  const max = Math.max(previousFocused, state.focusedRow);

                  for (let i = min; i <= max; i++) {
                    state.selectedRows.push(i);
                  }
                } else {
                  state.selectedRows = [state.focusedRow];
                }
                break;
              case 'Enter': {
                state.isEditionMode = true;
                this.setState(state);
                applyFocusAttributes(table, this.getState(), state);

                if (state.focusedRow >= 0) {
                  this.events.onSelectRows?.(
                    this.getSelection().map((index) => ({
                      index,
                      row: this.getState().rows[index],
                    })),
                    state.focusedRow as number,
                  );
                }
                return;
              }
            }

            state.isFocused = true;

            applySelectionAttributes(table, this.getState(), state);
            applyFocusAttributes(table, this.getState(), state);
            this.setState(state);
          }
        }
      }
    }
  }
}
